import React from "react";
import Link from "next/link";
import { Clock, Languages, Star } from "lucide-react";
import UserAvatar from "@/src/shared/components/atoms/UserAvatar";
import { formatReviewAverage } from "@/src/shared/utils/formatReviewAverage";
import SubtleButton from "@/src/shared/components/atoms/SubtleButton";
import type { IListingCoHost, IListingHost } from "@/src/shared/types/listing";
import { listingService } from "../services";

interface IMeetYourHostProps {
  host: IListingHost;
  coHosts?: IListingCoHost[];
  // The listing's own reviews, as the app shows them on this card.
  reviewsAvg: number;
  reviewsCount: number;
  handleHostModalOpen: () => void;
}

// Shown for every host, as in the app: the public API carries no verification field.
const VerifiedPill = ({ className = "" }: { className?: string }) => (
  <span
    className={`w-max items-center gap-1 whitespace-nowrap rounded-full bg-[#008A05] px-2 py-1 text-13 font-medium leading-[14px] tracking-[0.2px] text-white ${className}`}
  >
    <img
      src="/icons/listing/listing_verified_badge.svg"
      alt=""
      className="h-[13px] w-[13px]"
    />
    Verified
  </span>
);

// Same icon tile as the amenities list, so the section reads as part of the page.
const Fact = ({
  icon,
  label,
  value,
  valueClassName = "text-ink-black",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClassName?: string;
}) => (
  <div className="flex items-center gap-3">
    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-neutral-100 bg-neutral-50 text-ink-dark">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-xs text-ink-gray">{label}</p>
      <p className={`text-sm font-medium ${valueClassName}`}>{value}</p>
    </div>
  </div>
);

const MeetYourHost: React.FC<IMeetYourHostProps> = ({
  host,
  coHosts = [],
  reviewsAvg,
  reviewsCount,
  handleHostModalOpen,
}) => {
  const firstName = host.first_name ?? "";
  const lastName = host.last_name ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  const hostingSince =
    host.years_hosting != null
      ? new Date().getFullYear() - host.years_hosting
      : null;
  const responseLabel = listingService.responseTimeLabel(host.response);
  const tenure = listingService.hostTenure(
    host.years_hosting,
    new Date().getFullYear(),
  );

  const nameLink = (
    <Link
      href={`/host-details/${host.id}`}
      title={fullName}
      className="block max-w-full"
    >
      <h3 className="line-clamp-2 cursor-pointer break-words text-center text-lg font-semibold leading-snug text-ink-black transition-colors hover:text-coral-pink-600 hover:underline md:text-left">
        {fullName}
      </h3>
    </Link>
  );

  return (
    <section>
      <h2 className="mb-6 text-xl font-semibold text-ink md:text-22">
        Meet Your Host
      </h2>

      {/* Mobile: the app's centred card. md+: card and facts side by side. */}
      <div className="flex flex-col gap-5 md:max-w-3xl md:flex-row md:items-stretch md:gap-8">
        {reviewsCount > 0 ? (
          <div className="flex items-center gap-4 rounded-2xl bg-white px-5 py-6 shadow-[0_2px_16px_rgba(0,0,0,0.08)] md:w-[26rem] md:flex-shrink-0 md:border md:border-neutral-200 md:shadow-none">
            <div className="flex min-w-0 flex-1 flex-col items-center">
              <div className="relative mb-5">
                <UserAvatar
                  imageUrl={host.image?.url}
                  firstName={firstName}
                  lastName={lastName}
                  size="xl"
                />
                <VerifiedPill className="absolute left-1/2 -bottom-3 flex -translate-x-1/2 border-2 border-white" />
              </div>
              {nameLink}
            </div>

            <dl className="w-32 flex-shrink-0">
              <div className="border-b border-neutral-200 pb-3">
                <dt className="sr-only">Rating</dt>
                <dd className="flex items-center gap-1.5 text-2xl font-semibold text-ink-black">
                  <Star className="h-4 w-4 fill-current" />
                  {formatReviewAverage(reviewsAvg)}
                </dd>
                <dd className="mt-1 text-sm text-ink-gray">
                  {reviewsCount} {reviewsCount === 1 ? "Review" : "Reviews"}
                </dd>
              </div>
              <div className="pt-3">
                <dt className="sr-only">Time on Travela</dt>
                {"years" in tenure ? (
                  <>
                    <dd className="text-2xl font-semibold text-ink-black">
                      {tenure.years}
                    </dd>
                    <dd className="mt-1 text-sm text-ink-gray">
                      {tenure.years === 1 ? "Year" : "Years"} on Travela
                    </dd>
                  </>
                ) : (
                  // Under a year, as the app: no "0 Years on Travela".
                  <dd className="text-sm leading-5 text-ink-gray">
                    Started hosting since{" "}
                    <span className="font-semibold text-ink-black">
                      {tenure.since}
                    </span>
                  </dd>
                )}
              </div>
            </dl>
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-2xl bg-white px-6 pt-6 pb-5 shadow-[0_2px_16px_rgba(0,0,0,0.08)] md:w-80 md:flex-shrink-0 md:flex-row md:items-center md:gap-4 md:p-5 md:shadow-none md:border md:border-neutral-200">
            <div className="relative mb-5 md:mb-0 md:flex-shrink-0">
              <UserAvatar
                imageUrl={host.image?.url}
                firstName={firstName}
                lastName={lastName}
                size="xl"
                className="md:h-20 md:w-20"
              />
              <VerifiedPill className="absolute left-1/2 -bottom-3 flex -translate-x-1/2 border-2 border-white md:hidden" />
            </div>

            <div className="flex w-full min-w-0 flex-col items-center md:flex-1 md:items-start">
              {nameLink}
              <VerifiedPill className="mt-1 hidden md:inline-flex" />
              {hostingSince != null && (
                <p className="mt-0.5 text-sm text-ink-gray md:mt-1.5">
                  <span className="md:hidden">Started hosting</span>
                  <span className="hidden md:inline">Hosting</span> since{" "}
                  {hostingSince}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Mobile keeps the app's plain lines. */}
        <div className="space-y-2 text-base md:hidden">
          <p className="text-ink-black">Language : Bangla, English</p>
          <p className="font-medium text-success-verified">
            Response time: {responseLabel}
          </p>
        </div>

        <div className="hidden flex-1 flex-col justify-between gap-5 md:flex">
          <div className="flex flex-col gap-4">
            <Fact
              icon={<Clock className="h-5 w-5 text-success-verified" />}
              label="Response time"
              value={responseLabel}
              valueClassName="text-success-verified"
            />
            <Fact
              icon={<Languages className="h-5 w-5" />}
              label="Languages"
              value="Bangla, English"
            />
          </div>

          <SubtleButton onClick={handleHostModalOpen} className="self-start">
            Message host
          </SubtleButton>
        </div>

        <SubtleButton onClick={handleHostModalOpen} className="md:hidden">
          Message host
        </SubtleButton>
      </div>

      {coHosts.length > 0 && (
        <div className="mt-6">
          <p className="text-base font-medium text-ink">
            {coHosts.length === 1 ? "Co-host" : "Co-hosts"}
          </p>
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-3">
            {coHosts.map((coHost) => {
              const name = [coHost.first_name, coHost.last_name]
                .filter(Boolean)
                .join(" ");
              return (
                <li key={coHost.id} className="flex items-center gap-2">
                  <UserAvatar
                    imageUrl={coHost.image?.url}
                    firstName={coHost.first_name ?? ""}
                    lastName={coHost.last_name ?? ""}
                    size="sm"
                  />
                  <span className="text-base font-medium text-ink">{name}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default MeetYourHost;
